import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';

const DATA = '/blog/comma-compression/data/videos';

interface PanelSpec {
  src: string;
  label: string;
}

interface ScrubSet {
  panels: PanelSpec[];
}

const SETS: Record<string, ScrubSet> = {
  reconstruction: {
    panels: [
      { src: `${DATA}/reconstruction-original.mp4`, label: 'Original' },
      { src: `${DATA}/reconstruction-ours.mp4`, label: 'Ours (178 KB archive)' },
    ],
  },
  'seg-disagreement': {
    panels: [
      { src: `${DATA}/disagreement-where.mp4`, label: 'Where SegNet disagrees' },
      { src: `${DATA}/disagreement-overlay.mp4`, label: 'Disagreements over our SegNet output' },
    ],
  },
};

const Wrapper = styled.div`
  margin: 2rem 0;
  padding: 1rem;
  background: var(--bg-alt);
  border-radius: 6px;
`;

const PanelGrid = styled.div<{ panelCount: number }>`
  display: grid;
  grid-template-columns: repeat(${({ panelCount }) => panelCount}, 1fr);
  gap: 0.75rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const PanelLabel = styled.div`
  font-family: var(--font-mono);
  font-size: 0.68rem;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  text-transform: uppercase;
`;

const Vid = styled.video`
  width: 100%;
  display: block;
  border-radius: 3px;
  background: #000;
`;

const Controls = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-top: 1rem;
`;

const PlayButton = styled.button`
  font-family: var(--font-mono);
  font-size: 0.68rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.4rem 0.8rem;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--text);
  border-radius: 3px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const Scrub = styled.input`
  flex: 1;
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  background: var(--border);
  border-radius: 2px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    background: var(--accent);
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid var(--bg-alt);
  }
  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    background: var(--accent);
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid var(--bg-alt);
  }
`;

const TimeDisplay = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  color: var(--text-muted);
  white-space: nowrap;
  min-width: 4.5em;
  text-align: right;
`;

const formatTime = (s: number) => {
  if (!isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const SyncedScrubber: React.FC<{ source?: string }> = ({ source = 'reconstruction' }) => {
  const set = SETS[source];
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  // Keep all videos synchronized to the master (first) video's currentTime.
  useEffect(() => {
    const master = videoRefs.current[0];
    if (!master) return;

    const onTime = () => {
      setCurrentTime(master.currentTime);
      videoRefs.current.forEach((v, i) => {
        if (i === 0 || !v) return;
        if (Math.abs(v.currentTime - master.currentTime) > 0.08) {
          v.currentTime = master.currentTime;
        }
      });
    };
    const onMeta = () => {
      setDuration(master.duration || 0);
      // Safari with preload="metadata" doesn't render the first frame until play.
      // A tiny seek forces it to decode and paint frame 0.
      videoRefs.current.forEach((v) => {
        if (v && v.currentTime === 0) v.currentTime = 0.001;
      });
    };
    const onPlay = () => {
      setPlaying(true);
      videoRefs.current.forEach((v, i) => {
        if (i !== 0 && v) v.play().catch(() => {});
      });
    };
    const onPause = () => {
      setPlaying(false);
      videoRefs.current.forEach((v, i) => {
        if (i !== 0 && v) v.pause();
      });
    };

    master.addEventListener('timeupdate', onTime);
    master.addEventListener('loadedmetadata', onMeta);
    master.addEventListener('play', onPlay);
    master.addEventListener('pause', onPause);
    if (master.duration) onMeta();

    return () => {
      master.removeEventListener('timeupdate', onTime);
      master.removeEventListener('loadedmetadata', onMeta);
      master.removeEventListener('play', onPlay);
      master.removeEventListener('pause', onPause);
    };
  }, [source]);

  if (!set) {
    return <div>Unknown scrubber source: {source}</div>;
  }

  const togglePlay = () => {
    const master = videoRefs.current[0];
    if (!master) return;
    if (master.paused) master.play();
    else master.pause();
  };

  const onScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = parseFloat(e.target.value);
    videoRefs.current.forEach((v) => {
      if (v) v.currentTime = t;
    });
    setCurrentTime(t);
  };

  return (
    <Wrapper>
      <PanelGrid panelCount={set.panels.length}>
        {set.panels.map((p, i) => (
          <Panel key={p.src}>
            <PanelLabel>{p.label}</PanelLabel>
            <Vid
              ref={(el) => { videoRefs.current[i] = el; }}
              src={p.src}
              preload="metadata"
              playsInline
              muted
            />
          </Panel>
        ))}
      </PanelGrid>
      <Controls>
        <PlayButton onClick={togglePlay}>{playing ? 'Pause' : 'Play'}</PlayButton>
        <Scrub
          type="range"
          min={0}
          max={duration || 0}
          step={0.05}
          value={currentTime}
          onChange={onScrub}
          aria-label="Video position"
        />
        <TimeDisplay>{formatTime(currentTime)} / {formatTime(duration)}</TimeDisplay>
      </Controls>
    </Wrapper>
  );
};

export default SyncedScrubber;
